import type { McpAssistantConnection } from '@brickup/shared/dto'
import { MCP, scopeLabel } from '@brickup/shared/locales/de/mcp'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { formatDateTime } from '@brickup/shared/utils'
import { LinkRowList } from '@/components/Internal/LinkRow'
import { useRunsAccount, useViewerAccount } from '@/hooks/use-account-access'
import { metaLine } from '@/utils/meta-line'

const COPY = MCP.connections

/**
 * The assistants currently reaching the app: which account each one acts as,
 * what it may do, and when it last renewed.
 *
 * Rendered by the System page and by the admin dashboard, so the two cannot
 * word the same list two ways.
 *
 * The gate sits here so both surfaces hold one line: an assistant acts with
 * the role of the account that connected it, so cutting its connection is a
 * change to that account — the caller's own, and every account they run.
 */
export function McpConnectionList({
  connections,
  onRevoke,
}: {
  connections: McpAssistantConnection[]
  /** Left out by a surface that only reports. */
  onRevoke?: (connection: McpAssistantConnection) => void
}) {
  const runsAccount = useRunsAccount()
  const viewer = useViewerAccount()

  const mayCut = (connection: McpAssistantConnection) =>
    connection.userId === viewer?.id || runsAccount(connection.userRoles)

  return (
    <LinkRowList
      rows={connections.map(connection => ({
        key: `${connection.userId}-${connection.clientId}`,
        title: metaLine(connection.userName, connection.clientName),
        subtitle: metaLine(
          connection.scopes.map(scopeLabel).join(', '),
          STAFF_DASHBOARD.lastActive(formatDateTime(connection.lastActiveAt)),
        ),
        removeLabel: COPY.revoke,
        onRemove:
          onRevoke && mayCut(connection)
            ? () => onRevoke(connection)
            : undefined,
      }))}
      emptyTitle={COPY.empty}
    />
  )
}
